import { ExtendedTarificationFormData } from '@/types/benefits/tarification/TarificationFormData';
import {
  ArrayPath,
  type Control,
  FieldArray,
  Path,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import StyledButton from '../styled/StyledButton';
import StyledCheckboxGroup from '../styled/StyledCheckboxGroup';
import StyledDatePicker from '../styled/StyledDatePicker';
import { Label } from '@/components/ui/label';
import PlusIcon from '@/assets/icons/plus.svg?react';
import clsx from 'clsx';
import { addDays, startOfDay } from 'date-fns';
import { useState } from 'react';
import { WEEKDAYS, WEEKDAYS_TRANSLATED } from '@/constants/weekdays';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';

const NestedTarifPeriods = <FieldsType extends ExtendedTarificationFormData>({
  control,
  tarifIndex,
  isViewMode = false,
}: {
  control: Control<FieldsType>;
  tarifIndex: number;
  isViewMode?: boolean;
}) => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const fieldName = `tarifs.${tarifIndex}.periods` as ArrayPath<FieldsType>;

  const { fields, remove, append } = useFieldArray({
    control,
    name: fieldName,
  });

  const firstPeriodApplicationDays = useWatch({
    control,
    name: `tarifs.${tarifIndex}.periods.0.applicationDays` as Path<FieldsType>,
  });

  const { watch } = useFormContext();
  const periodValues = watch(fieldName);

  const getStartDateValue = (index: number, add = 0) => {
    return periodValues && periodValues[index]?.startDate
      ? addDays(new Date(periodValues[index]?.startDate), add)
      : undefined;
  };

  const getEndDateValue = (index: number, add = 0) => {
    return periodValues && periodValues[index]?.endDate
      ? addDays(new Date(periodValues[index]?.endDate), add)
      : undefined;
  };

  const [showPastPeriods, setShowPastPeriods] = useState(false);
  const today = startOfDay(new Date());
  // Past periods stay in the form state (so they are still saved and
  // validated) but are not rendered unless asked.
  const isPastPeriod = (index: number) => {
    const endDate = periodValues?.[index]?.endDate;
    return !!endDate && startOfDay(new Date(endDate)) < today;
  };
  const pastPeriodsCount = fields.filter((_, index) =>
    isPastPeriod(index),
  ).length;

  return (
    <>
      {pastPeriodsCount > 0 && (
        <StyledButton
          className="p-0 h-auto w-fit"
          variant="link"
          title={
            showPastPeriods
              ? t('tariffs.hidePastPeriods')
              : t('tariffs.showPastPeriods', { count: pastPeriodsCount })
          }
          onClick={() => setShowPastPeriods((value) => !value)}
        />
      )}
      {fields.map((item, index) =>
        !showPastPeriods && isPastPeriod(index) ? null : (
          <div className="mb-1" key={item.id}>
            <div
              className={clsx('flex flex-col', {
                'w-full gap-1.5': !isViewMode,
                'lg:flex-row md:justify-between gap-6': isViewMode,
              })}
            >
              <div className={clsx({ 'flex flex-col w-full': isViewMode })}>
                <div
                  className={clsx({
                    'flex justify-between': !isViewMode,
                  })}
                >
                  <Label className="text-gray-700">
                    {t('tariffs.period_', {
                      index: index + 1,
                      tarifIndex: tarifIndex + 1,
                    })}
                  </Label>
                  {fields.length > 1 && !isViewMode && (
                    <StyledButton
                      title={t('common.delete')}
                      className="p-0 h-auto"
                      variant="link"
                      onClick={() => remove(index)}
                    />
                  )}
                </div>
                <div
                  className={clsx('flex items-center gap-2', {
                    'flex-col md:flex-row w-full': !isViewMode,
                  })}
                >
                  <span>{t('common.from')}</span>
                  <StyledDatePicker
                    control={control}
                    name={`${fieldName}.${index}.startDate` as Path<FieldsType>}
                    className="w-full"
                    isViewMode={isViewMode}
                    earliestDate={getEndDateValue(index - 1, 1)}
                    enforceCurrentDateBoundary={false}
                  />
                  <span>{t('common.to')}</span>
                  <StyledDatePicker
                    control={control}
                    name={`${fieldName}.${index}.endDate` as Path<FieldsType>}
                    className="w-full"
                    isViewMode={isViewMode}
                    earliestDate={getStartDateValue(index, 0)}
                    enforceCurrentDateBoundary={false}
                  />
                </div>
              </div>
              <div className="flex flex-col w-full gap-1.5">
                <Label className="text-gray-700">
                  {t('tariffs.validDays')} *
                </Label>
                <StyledCheckboxGroup
                  control={control}
                  name={
                    `${fieldName}.${index}.applicationDays` as Path<FieldsType>
                  }
                  options={WEEKDAYS.map((weekday) => ({
                    value: weekday,
                    label: getTranslatedConst(WEEKDAYS_TRANSLATED)[weekday][0],
                  }))}
                  isViewMode={isViewMode}
                />
              </div>
            </div>
          </div>
        ),
      )}
      {!isViewMode && (
        <StyledButton
          className="text-brand-600 w-fit pl-0"
          title={t('tariffs.addPeriod')}
          onClick={() => {
            append({
              startDate: undefined,
              endDate: undefined,
              applicationDays: firstPeriodApplicationDays,
            } as FieldArray<FieldsType, ArrayPath<FieldsType>>[][0]);
          }}
          variant="link"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
        />
      )}
    </>
  );
};

export default NestedTarifPeriods;
